import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MitigationStatus } from 'shared';

interface StatusTransitionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
  fromStatus: MitigationStatus;
  toStatus: MitigationStatus;
  isSubmitting?: boolean;
}

const STATUS_COLORS: Record<
  MitigationStatus,
  'default' | 'info' | 'warning' | 'success'
> = {
  [MitigationStatus.PLANNED]: 'default',
  [MitigationStatus.IN_PROGRESS]: 'info',
  [MitigationStatus.IMPLEMENTED]: 'warning',
  [MitigationStatus.VERIFIED]: 'success',
};

export const StatusTransitionDialog: React.FC<StatusTransitionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  fromStatus,
  toStatus,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');

  const handleConfirm = () => {
    onConfirm(comment);
    setComment(''); // Reset for next use
  };

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const getStatusLabel = (status: MitigationStatus): string => {
    return t(`mitigation.status.${status}`);
  };

  // Check if this is a "regressive" status change
  const statusOrder = [
    MitigationStatus.PLANNED,
    MitigationStatus.IN_PROGRESS,
    MitigationStatus.IMPLEMENTED,
    MitigationStatus.VERIFIED,
  ];
  const fromIndex = statusOrder.indexOf(fromStatus);
  const toIndex = statusOrder.indexOf(toStatus);
  const isRegressive = toIndex < fromIndex;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mitigation.statusChange.title')}</DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={2}
          my={2}
        >
          <Chip
            label={getStatusLabel(fromStatus)}
            color={STATUS_COLORS[fromStatus]}
            variant="outlined"
          />
          <ArrowIcon color="action" />
          <Chip
            label={getStatusLabel(toStatus)}
            color={STATUS_COLORS[toStatus]}
          />
        </Box>

        {isRegressive && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              'mitigation.statusChange.regressiveWarning',
              'You are moving the status backwards. Please explain why.'
            )}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('mitigation.statusChange.addComment')}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={3}
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder={t('mitigation.statusChange.commentPlaceholder')}
          variant="outlined"
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isSubmitting || (isRegressive && !comment.trim())}
        >
          {isSubmitting
            ? t('common.saving')
            : t('mitigation.statusChange.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusTransitionDialog;
